import React, { useState } from 'react'
import { Box, IconButton, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material'
import { 
  ZoomOutMap as ResetZoomIcon
} from '@mui/icons-material'
import type { SciChartSurface } from 'scichart'

export type ZoomMode = 'box' | 'sliceX' | 'sliceY'

export interface ZoomControlsProps {
  mode?: ZoomMode
  onModeChange?: (mode: ZoomMode) => void
  chartSurfaces?: SciChartSurface[]
  onResetZoom?: () => void
  disabled?: boolean
  theme?: 'dark' | 'light'
}

export const ZoomControls: React.FC<ZoomControlsProps> = ({
  mode = 'sliceX',
  onModeChange,
  chartSurfaces = [],
  onResetZoom,
  disabled = false,
  theme = 'dark'
}) => {
  const [currentMode, setCurrentMode] = useState<ZoomMode>(mode)

  const handleModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: ZoomMode | null,
  ) => {
    if (newMode !== null && newMode !== currentMode) {
      setCurrentMode(newMode)
      onModeChange?.(newMode)
    }
  }

  const handleResetZoom = () => {
    chartSurfaces.forEach(surface => {
      surface.zoomExtents()
    })
    onResetZoom?.()
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {/* Zoom Mode Selector */}
      <ToggleButtonGroup
        value={currentMode}
        exclusive
        onChange={handleModeChange}
        size="small"
        disabled={disabled}
        sx={{
          height: '32px',
          '& .MuiToggleButton-root': {
            padding: '6px 10px',
            minWidth: '28px',
            height: '32px',
            border: '1px solid',
            borderColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
            backgroundColor: 'transparent',
            color: theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
            '&:hover': {
              backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)',
            },
            '&.Mui-selected': {
              backgroundColor: 'rgba(25, 118, 210, 0.8)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.9)',
              }
            }
          }
        }}
      >
        <ToggleButton value="box" aria-label="Box zoom">
          <Tooltip title="Box Zoom - Drag to select area" placement="top">
            <svg width="22" height="22" viewBox="0 0 16 16" fill="currentColor">
              {/* Main rectangle outline */}
              <rect x="3" y="3" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1" />
              {/* Corner selection markers */}
              <rect x="2.5" y="2.5" width="2" height="2" fill="none" stroke="currentColor" strokeWidth="1" />
              <rect x="11.5" y="2.5" width="2" height="2" fill="none" stroke="currentColor" strokeWidth="1" />
              <rect x="2.5" y="11.5" width="2" height="2" fill="none" stroke="currentColor" strokeWidth="1" />
              <rect x="11.5" y="11.5" width="2" height="2" fill="none" stroke="currentColor" strokeWidth="1" />
            </svg>
          </Tooltip>
        </ToggleButton>
        
        <ToggleButton value="sliceX" aria-label="Horizontal slice zoom">
          <Tooltip title="Slice X - Horizontal zoom only" placement="top">
            <svg width="22" height="22" viewBox="0 0 16 16" fill="currentColor">
              <rect x="2" y="2" width="1" height="12" />
              <rect x="13" y="2" width="1" height="12" />
              <polygon points="7,8 5,6 5,10" />
              <polygon points="9,8 11,6 11,10" />
            </svg>
          </Tooltip>
        </ToggleButton>
        
        <ToggleButton value="sliceY" aria-label="Vertical slice zoom">
          <Tooltip title="Slice Y - Vertical zoom only" placement="top">
            <svg width="22" height="22" viewBox="0 0 16 16" fill="currentColor">
              <rect x="2" y="2" width="12" height="1" />
              <rect x="2" y="13" width="12" height="1" />
              <polygon points="8,7 6,5 10,5" />
              <polygon points="8,9 6,11 10,11" />
            </svg>
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>

      {/* Reset Zoom Button */}
      <Tooltip title="Reset Zoom" placement="top">
        <IconButton
          size="small"
          onClick={handleResetZoom}
          disabled={disabled}
          sx={{
            width: '32px',
            height: '32px',
            border: '1px solid',
            borderColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
            backgroundColor: 'transparent',
            color: theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)',
            }
          }}
        >
          <ResetZoomIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  )
} 